import React, { useMemo } from "react";
import { printThermalReceipt } from "../../utils/thermoprint";
import { getAmountInCurrencyFormat } from "../../utils/number";
import { fmt } from "../../utils/helper";

const nv = (v) => parseFloat(v) || 0;

// ─────────────────────────────────────────────────────────────────────────────
// BALANCE SIGN CONVENTION  (mirrors backend customer.balanceType)
//
//   customer.balance > 0  →  "payable"    → admin owes customer  → display +  (blue)
//   customer.balance < 0  →  "receivable" → customer owes admin  → display −  (red)
//
// Each charge row carries a `chargeSign` that contributes to amountDelta:
//   +1  →  admin owes customer  (receive section, non-lab/ginni)
//   -1  →  customer owes admin  (given section non-lab/ginni,  OR  lab/ginni anywhere)
//
// Row flags:
//   isCharge  — amount sits in Cash col as a charge (amber = customer owes admin,
//               blue-tinted = admin owes customer)
//   chargeSign — +1 or -1 (only meaningful when isCharge = true)
//   isPaid    — paid sub-row (green, no serial)
// ─────────────────────────────────────────────────────────────────────────────

export function deriveRows(tx, side) {
  const isReceive = side === "receive";
  const rows = [];

  const pushItem = (name, weight, amount, isCharge = false, chargeSign = 1) => {
    if (nv(weight) > 0 || nv(amount) > 0) {
      rows.push({
        name,
        weight: nv(weight),
        amount: nv(amount),
        isPaid: false,
        isCharge,
        chargeSign,
        isSerialized: true,
      });
    }
  };

  const pushPaid = (name, amount) => {
    if (nv(amount) > 0) {
      rows.push({
        name,
        weight: 0,
        amount: nv(amount),
        isPaid: true,
        isCharge: false,
        chargeSign: 0,
        isSerialized: false,
      });
    }
  };

  const pushSubItem = (name, weight, amount) => {
    if (nv(weight) > 0 || nv(amount) > 0) {
      rows.push({
        name,
        weight: nv(weight),
        amount: nv(amount),
        isPaid: false,
        isCharge: false,
        chargeSign: 0,
        isSerialized: false,
        isSubItem: true,
      });
    }
  };

  // ── CASH ──────────────────────────────────────────────────────────────
  pushItem("Cash", 0, tx.cash?.amount, false);

  // ── PC ────────────────────────────────────────────────────────────────
  const pcTxs = tx.pc?.transactions || [];
  if (pcTxs.length) {
    const totalWeight = pcTxs.reduce((s, t) => s + nv(t.totalWeight), 0);
    const totalAmount = pcTxs.reduce((s, t) => s + nv(t.totalAmount), 0);
    pushItem(
      "PC",
      totalWeight,
      totalAmount,
      totalAmount > 0,
      isReceive ? 1 : -1,
    );
    pcTxs.forEach((t, i) => {
      pushSubItem(
        `  ↳ PC #${i + 1}${t.brand && t.brand !== "Select" ? ` (${t.brand})` : ""}`,
        t.totalWeight,
        t.totalAmount,
      );
    });
    pcTxs.forEach((t, i) => {
      // paid reduces what customer owes (or what admin owes)
      pushPaid(`  ↳ PC #${i + 1} Paid`, nv(tx.pc.paidAmounts?.[t.id]));
    });
  }

  // ── LAB — charges ALWAYS imposed on customer, chargeSign always -1 ────
  const labTxs = tx.lab?.transactions || [];
  if (labTxs.length) {
    const totalWeight = labTxs.reduce((s, t) => s + nv(t.pureWeight), 0);
    const totalAmount = labTxs.reduce((s, t) => s + nv(t.totalAmount), 0);
    pushItem("Lab", totalWeight, totalAmount, totalAmount > 0, -1); // always -1
    labTxs.forEach((t, i) => {
      pushSubItem(
        `  ↳ Lab #${i + 1} ${t.report ? `Report: ${t.report} total Weight: ${fmt(t.weight)}g` : "Without report"}`,
        t.pureWeight,
        t.totalAmount,
      );
    });
    labTxs.forEach((t, i) => {
      pushPaid(`  ↳ Lab #${i + 1} Paid`, nv(tx.lab.paidAmounts?.[t.id]));
    });
  }

  // ── BAR ───────────────────────────────────────────────────────────────
  const barTxs = tx.bar?.transactions || [];
  if (barTxs.length) {
    const totalWeight = barTxs.reduce((s, t) => s + nv(t.totalWeight), 0);
    const totalAmount = barTxs.reduce((s, t) => s + nv(t.totalAmount), 0);
    pushItem(
      "Bar",
      totalWeight,
      totalAmount,
      totalAmount > 0,
      isReceive ? 1 : -1,
    );
    barTxs.forEach((t, i) => {
      pushSubItem(
        `  ↳ Bar #${i + 1}${t.brand && t.brand !== "add-new" ? ` (${t.brand})` : ""}`,
        t.totalWeight,
        t.totalAmount,
      );
    });
    barTxs.forEach((t, i) => {
      pushPaid(`  ↳ Bar #${i + 1} Paid`, nv(tx.bar.paidAmounts?.[t.id]));
    });
  }

  // ── COIN ──────────────────────────────────────────────────────────────
  const coinTxs = tx.coin?.transactions || [];
  if (coinTxs.length) {
    const totalWeight = coinTxs.reduce((s, t) => s + nv(t.totalWeight), 0);
    const totalAmount = coinTxs.reduce((s, t) => s + nv(t.totalAmount), 0);
    pushItem(
      "Coin",
      totalWeight,
      totalAmount,
      totalAmount > 0,
      isReceive ? 1 : -1,
    );
    coinTxs.forEach((t, i) => {
      pushSubItem(
        `  ↳ Coin #${i + 1}${t.brand && t.brand !== "add-new" ? ` (${t.brand})` : ""}`,
        t.totalWeight,
        t.totalAmount,
      );
    });
    coinTxs.forEach((t, i) => {
      pushPaid(`  ↳ Coin #${i + 1} Paid`, nv(tx.coin.paidAmounts?.[t.id]));
    });
  }

  // ── GINNI — charges ALWAYS imposed on customer, chargeSign always -1 ──
  const ginniTxs = tx.ginni?.transactions || [];
  if (ginniTxs.length) {
    const totalWeight = ginniTxs.reduce((s, t) => s + nv(t.pureWeight), 0);
    const totalAmount = ginniTxs.reduce((s, t) => s + nv(t.totalAmount), 0);
    pushItem("Ginni", totalWeight, totalAmount, totalAmount > 0, -1); // always -1
    ginniTxs.forEach((t, i) => {
      pushSubItem(
        `  ↳ Ginni #${i + 1} ${t.report ? `Report: ${t.report} total Weight: ${fmt(t.weight)}g` : "Without report"}`,
        t.pureWeight,
        t.totalAmount,
      );
    });
    ginniTxs.forEach((t, i) => {
      pushPaid(`  ↳ Ginni #${i + 1} Paid`, nv(tx.ginni.paidAmounts?.[t.id]));
    });
  }

  // ── SILVER ────────────────────────────────────────────────────────────
  const silverTxs = tx.silver?.transactions || [];
  if (silverTxs.length) {
    const totalWeight = silverTxs.reduce((s, t) => s + nv(t.totalWeight), 0);
    const totalAmount = silverTxs.reduce((s, t) => s + nv(t.totalAmount), 0);
    pushItem(
      "Silver",
      totalWeight,
      totalAmount,
      totalAmount > 0,
      isReceive ? 1 : -1,
    );
    silverTxs.forEach((t, i) => {
      pushSubItem(
        `  ↳ Silver #${i + 1}${t.brand ? ` (${t.brand})` : ""}`,
        t.totalWeight,
        t.totalAmount,
      );
    });
    silverTxs.forEach((t, i) => {
      pushPaid(`  ↳ Silver #${i + 1} Paid`, nv(tx.silver.paidAmounts?.[t.id]));
    });
  }

  // ── GOLD / SILVER RATE WORK — unchanged ───────────────────────────────
  if (nv(tx.goldRW?.weight) > 0 || nv(tx.goldRW?.totalAmount) > 0)
    pushItem(
      "Gold - Rate of Work",
      tx.goldRW?.weight,
      tx.goldRW?.totalAmount,
      false,
    );
  if (nv(tx.silverRW?.weight) > 0 || nv(tx.silverRW?.totalAmount) > 0)
    pushItem(
      "Silver - Rate of Work",
      tx.silverRW?.weight,
      tx.silverRW?.totalAmount,
      false,
    );

  return rows;
}

// ─── Totals ───────────────────────────────────────────────────────────────────
// amountDelta mirrors backend's amountDelta accumulation:
//   charge row: chargeSign * dueAmount   where dueAmount = amount − paid
//   paid rows reduce the dueAmount so they are folded into their parent's net
//
// We track:
//   totalCash     — plain cash (no charge, no paid)
//   amountDelta   — net signed balance contribution from all charges
//   totalPaid     — sum of paid sub-rows (for display only — already folded into amountDelta)
function computeTotals(rows) {
  let result = { weight: 0, totalCash: 0, amountDelta: 0, totalPaid: 0 };

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];

    // sub-items are display-only — skip entirely
    if (r.isSubItem) continue;

    if (r.isPaid) {
      const parentCharge = [...rows]
        .slice(0, i)
        .filter((x) => !x.isSubItem) // ← skip sub-items when finding parent
        .reverse()
        .find((x) => x.isCharge);
      if (parentCharge) {
        result.amountDelta -= parentCharge.chargeSign * r.amount;
      }
      result.totalPaid += r.amount;
      continue;
    }

    result.weight += r.weight;

    if (r.isCharge) {
      result.amountDelta += r.chargeSign * r.amount;
    } else {
      result.totalCash += r.amount;
    }
  }

  return result;
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const sectionHeaderStyle = {
  background: "linear-gradient(135deg,#d4853a 0%,#b8751a 100%)",
  color: "white",
  fontWeight: "bold",
};
const totalRowStyle = { backgroundColor: "#f5f5f5", fontWeight: "bold" };
const cellBase = { border: "1px solid #d4853a", padding: 12 };
const cellCenter = { ...cellBase, textAlign: "center" };

// charge owed BY customer TO admin (chargeSign = -1) → red-amber
// color: "#b8751a"
const chargeDebitCellBase = { ...cellBase };
const chargeDebitCellCtr = { ...cellCenter };

// charge owed BY admin TO customer (chargeSign = +1) → blue-tinted
// color: "#0d6efd"
const chargeCreditCellBase = { ...cellBase };
const chargeCreditCellCtr = { ...cellCenter };

// paid row — green, indented
const paidCellBase = {
  ...cellBase,
  // color: "#198754",
  // fontStyle: "italic",
  // background: "#f0fdf4",
  fontSize: 12,
};
const paidCellCtr = {
  ...cellCenter,
  // color: "#198754",
  // fontStyle: "italic",
  // background: "#f0fdf4",
  fontSize: 12,
};

// ─── DataRow ──────────────────────────────────────────────────────────────────
function DataRow({
  serialIndex,
  name,
  weight,
  amount,
  isPaid = false,
  isCharge = false,
  chargeSign = 1,
  isSubItem,
}) {
  let cb, cc;
  if (isPaid || isSubItem) {
    cb = paidCellBase;
    cc = paidCellCtr;
  } else if (isCharge && chargeSign === -1) {
    cb = chargeDebitCellBase;
    cc = chargeDebitCellCtr;
  } else if (isCharge && chargeSign === 1) {
    cb = chargeCreditCellBase;
    cc = chargeCreditCellCtr;
  } else {
    cb = cellBase;
    cc = cellCenter;
  }

  return (
    <tr style={{ border: "1px solid #d4853a" }}>
      <td style={cc}>{isPaid ? "" : serialIndex}</td>
      <td style={cb}>{name}</td>
      <td style={cc}>{!isPaid && weight > 0 ? weight.toFixed(3) : ""}</td>
      <td style={cc}>{amount > 0 ? getAmountInCurrencyFormat(amount) : ""}</td>
    </tr>
  );
}

// ─── SaleTable ────────────────────────────────────────────────────────────────
function SaleTable({
  header = {},
  receive = {},
  given = {},
  onSubmit,
  loading = false,
  isView = false,
}) {
  const receiveRows = useMemo(() => deriveRows(receive, "receive"), [receive]);
  const givenRows = useMemo(() => deriveRows(given, "given"), [given]);

  const recvTotals = useMemo(() => computeTotals(receiveRows), [receiveRows]);
  const givnTotals = useMemo(() => computeTotals(givenRows), [givenRows]);

  // ── Balance (mirrors backend customer.balance accumulation) ──────────────
  //
  // cash:
  //   receive cash → +cashDelta  (admin holds it, owes customer)
  //   given cash   → -cashDelta  (admin paid out, customer balance goes down)
  //
  // charges (via amountDelta, already sign-correct per section/type):
  //   receive non-lab/ginni charge +  → admin owes customer
  //   given   non-lab/ginni charge −  → customer owes admin
  //   lab / ginni anywhere         −  → customer always owes admin
  //
  // net balance from CUSTOMER's POV:
  //   + → admin owes customer (payable)   → blue
  //   − → customer owes admin (receivable) → red

  const cashBalance = recvTotals.totalCash - givnTotals.totalCash; // recv cash in − given cash out
  const amountBalance = recvTotals.amountDelta + givnTotals.amountDelta; // signed charges net
  const netBalance = cashBalance + amountBalance;

  const handleThermoPrint = () => {
    printThermalReceipt({
      header,
      receiveRows,
      givenRows,
      recvTotals,
      givnTotals,
      cashBalance,
      amountBalance,
      netBalance,
    });
  };

  // Section total for Cash column = plain cash + abs(all charges) — for display
  const sectionCashTotal = (t) => {
    // sum of all charge amounts (unsigned, for display in section total row)
    return t.totalCash; // plain cash only in totals; charges shown per-row
  };

  // ── helper: render rows with correct serial numbers ───────────────────────
  const renderRows = (rows, prefix) => {
    let serial = 0;
    return rows.map((row, i) => {
      if (row.isSerialized) serial += 1;
      return (
        <DataRow
          key={`${prefix}-${i}`}
          serialIndex={row.isSerialized ? serial : undefined}
          {...row}
        />
      );
    });
  };

  // Section charge total (sum of all charge amounts, unsigned, for total row)
  const sectionChargeTotal = (rows) =>
    rows.reduce((s, r) => s + (r.isCharge ? r.amount : 0), 0);

  const recvDisplayTotal =
    recvTotals.totalCash + sectionChargeTotal(receiveRows);
  const givnDisplayTotal = givnTotals.totalCash + sectionChargeTotal(givenRows);

  const balColor =
    netBalance > 0 ? "#0d6efd" : netBalance < 0 ? "#dc3545" : "#198754";
  const balWeightVal = recvTotals.weight - givnTotals.weight;
  const wColor =
    balWeightVal > 0 ? "#0d6efd" : balWeightVal < 0 ? "#dc3545" : "#198754";

  return (
    <div className="container">
      <div className="row">
        <div className="col-12" id="table-container">
          {/* ── Header bar ── */}
          <div
            style={{
              background: "linear-gradient(135deg,#d4853a 0%,#b8751a 100%)",
              padding: "12px 20px",
              borderRadius: "8px 8px 0 0",
              display: "grid",
              gridTemplateColumns: "repeat(4, auto)",
              gap: 15,
              alignItems: "center",
              color: "white",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            {[
              {
                label: "Serial No",
                id: "tableSerialNoField",
                width: 100,
                val: header.serialNo,
              },
              {
                label: "Date",
                id: "tableDateField",
                width: 90,
                val: header.date,
              },
              {
                label: "Party Name",
                id: "tablePartyNameField",
                width: 140,
                val: header.partyName,
              },
              {
                label: "Time",
                id: "tableTimeField",
                width: 70,
                val: header.time,
              },
            ].map(({ label, id, width, val }) => (
              <div
                key={id}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <label style={{ margin: 0 }}>{label}</label>
                <input
                  type="text"
                  id={id}
                  readOnly
                  value={val || ""}
                  style={{
                    width,
                    padding: "5px 8px",
                    border: "none",
                    borderRadius: 4,
                    background: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                />
              </div>
            ))}
          </div>

          {/* ── Main table ── */}
          <table
            className="table custom-table"
            id="dataTable"
            style={{
              borderCollapse: "collapse",
              border: "2px solid #d4853a",
              width: "100%",
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: "#f5f5f5",
                  border: "2px solid #d4853a",
                }}
              >
                <th style={{ ...cellCenter, width: 60 }}>Serial No</th>
                <th style={cellBase}>Particular</th>
                <th style={{ ...cellCenter, width: 120 }}>Weight</th>
                <th style={{ ...cellCenter, width: 120 }}>Cash</th>
              </tr>
            </thead>

            <tbody>
              {/* ── RECEIVE ── */}
              <tr className="section-title" style={sectionHeaderStyle}>
                <td colSpan={4} style={{ ...cellCenter, fontSize: 16 }}>
                  Receive
                </td>
              </tr>
              <tr id="receiveRowsMarker" style={{ display: "none" }} />

              {renderRows(receiveRows, "recv")}

              <tr className="total-row" style={totalRowStyle}>
                <td colSpan={1} style={cellCenter}>
                  Total
                </td>
                <td style={cellBase} />
                <td style={cellCenter} id="receiveWeight">
                  {recvTotals.weight > 0 ? recvTotals.weight.toFixed(3) : ""}
                </td>
                <td style={cellCenter} id="receiveCash">
                  {recvDisplayTotal > 0
                    ? getAmountInCurrencyFormat(recvDisplayTotal)
                    : ""}
                </td>
              </tr>

              {/* ── GIVEN ── */}
              <tr className="section-title" style={sectionHeaderStyle}>
                <td colSpan={4} style={{ ...cellCenter, fontSize: 16 }}>
                  Given
                </td>
              </tr>
              <tr id="givenRowsMarker" style={{ display: "none" }} />

              {renderRows(givenRows, "givn")}

              <tr className="total-row" style={totalRowStyle}>
                <td colSpan={1} style={cellCenter}>
                  Total
                </td>
                <td style={cellBase} />
                <td style={cellCenter} id="givenWeight">
                  {givnTotals.weight > 0 ? givnTotals.weight.toFixed(3) : ""}
                </td>
                <td style={cellCenter} id="givenCash">
                  {givnDisplayTotal > 0
                    ? getAmountInCurrencyFormat(givnDisplayTotal)
                    : ""}
                </td>
              </tr>

              {/* ── BALANCE ── */}
              <tr className="balance-row" style={totalRowStyle}>
                <td colSpan={1} style={cellCenter}>
                  Total Balance
                </td>
                <td style={{ ...cellBase, fontSize: 11, color: "#888" }}>
                  {/* hint text: what the net balance means */}
                  {/* {netBalance > 0
                    ? "Admin owes customer"
                    : netBalance < 0
                      ? "Customer owes admin"
                      : "Settled ✓"} */}
                </td>
                <td
                  style={{ ...cellCenter, fontWeight: 700 }}
                  id="balanceWeight"
                >
                  {balWeightVal !== 0
                    ? `${balWeightVal > 0 ? "+" : ""}${balWeightVal.toFixed(3)}`
                    : ""}
                </td>
                <td style={{ ...cellCenter, fontWeight: 700 }} id="balanceCash">
                  {netBalance !== 0
                    ? `${netBalance > 0 ? "+" : ""}${getAmountInCurrencyFormat(netBalance)}`
                    : ""}
                </td>
              </tr>
            </tbody>
          </table>

          {/* ── Action buttons ── */}
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            {!isView && (
              <button
                className="btn-custom btn-submit"
                id="submitLedger"
                disabled={loading}
                onClick={onSubmit}
              >
                <span>{loading ? "Submitting..." : "Submit Entry"}</span>
              </button>
            )}
            <button
              className="btn-custom btn-thermo"
              id="thermoPrintBtn"
              onClick={handleThermoPrint}
            >
              <span>
                <i className="fa-solid fa-print" /> Thermo Print
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SaleTable;
